import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getStoredTimezone, getTimezoneAbbr } from "@/lib/timezone-utils";

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
  related_session_id?: string;
}

interface NotificationBellProps {
  onReviewNotificationClick?: (sessionId: string) => void;
}

export function NotificationBell({ onReviewNotificationClick }: NotificationBellProps = { onReviewNotificationClick: undefined }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [formattedMessages, setFormattedMessages] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useEffect(() => {
    fetchNotifications();
    
    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      // Subscribe to real-time notifications for the current user
      const channel = supabase
        .channel('notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            const newNotification = payload.new as Notification;
            console.log('New notification received:', newNotification);
            
            setNotifications(prev => {
              const updated = [newNotification, ...prev];
              console.log('Updated notifications:', updated);
              return updated;
            });
            
            // Ensure proper message formatting for session notifications
            formatSessionNotifications([newNotification]);
            
            setUnreadCount(prev => {
              const newCount = prev + 1;
              console.log('Updating unreadCount from', prev, 'to', newCount);
              return newCount;
            });
            
            toast({
              title: newNotification.title,
              description: formattedMessages[newNotification.id] || newNotification.message,
            });
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };
    
    const cleanup = setupRealtimeSubscription();
    
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
    };
  }, [toast]);

  const fetchNotifications = async () => {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching notifications:', error);
      return;
    }

    setNotifications(data || []);
    setUnreadCount(data?.filter(n => !n.is_read).length || 0);

    // Post-process to ensure correct time display for session notifications
    await formatSessionNotifications(data || []);
  };

  const markAsRead = async (notificationId: string) => {
    console.log('Marking notification as read:', notificationId);
    
    const { data, error } = await supabase
      .from('notifications')
      .update({ is_read: true, updated_at: new Date().toISOString() })
      .eq('id', notificationId)
      .select();

    if (error) {
      console.error('Error marking notification as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        variant: "destructive",
      });
      return;
    }

    console.log('Notification marked as read successfully:', data);

    // Update local state
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = async () => {
    console.log('Marking all notifications as read');
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('No user found');
      return;
    }

    const { data, error } = await supabase
      .from('notifications')
      .update({ is_read: true, updated_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('is_read', false)
      .select();

    if (error) {
      console.error('Error marking all notifications as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark all notifications as read",
        variant: "destructive",
      });
      return;
    }

    console.log('All notifications marked as read:', data);

    setNotifications(prev =>
      prev.map(n => ({ ...n, is_read: true }))
    );
    setUnreadCount(0);
    
    toast({
      title: "Success",
      description: "All notifications marked as read",
    });
  };

  const handleNotificationClick = async (notification: Notification) => {
    console.log('Notification clicked:', notification.id, 'is_read:', notification.is_read);
    // Mark as read first
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    // Handle review notifications specifically
    if (notification.title.includes('Rate Your Recent Session') && 
        notification.related_session_id && 
        onReviewNotificationClick) {
      onReviewNotificationClick(notification.related_session_id);
      setIsOpen(false); // Close the popover
    }
  };

  // Build a consistent, timezone-aware message for session notifications
  const formatSessionNotifications = async (list: Notification[]) => {
    const tz = getStoredTimezone();
    const tzAbbr = getTimezoneAbbr(tz);
    const targets = list.filter(n => n.related_session_id && (
      n.title.includes('Session Booked') || n.title.includes('New Session')
    ));
    if (targets.length === 0) return;

    const results: Record<string, string> = {};
    for (const n of targets) {
      const { data: session, error } = await supabase
        .from('sessions')
        .select('scheduled_at, mentors(first_name,last_name)')
        .eq('id', n.related_session_id as string)
        .single();
      if (error || !session) continue;
      const dt = new Date(session.scheduled_at);
      const dateStr = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: tz }).format(dt);
      const timeStr = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(dt);
      const mentorName = `${session.mentors?.first_name ?? ''} ${session.mentors?.last_name ?? ''}`.trim();
      if (n.title.includes('Session Booked')) {
        results[n.id] = `Your session with ${mentorName} has been scheduled for ${dateStr} at ${timeStr} ${tzAbbr}.`;
      } else if (n.title.includes('New Session')) {
        results[n.id] = `${mentorName ? mentorName : 'A student'} booked a session on ${dateStr} at ${timeStr} ${tzAbbr}.`;
      }
    }
    setFormattedMessages(prev => ({ ...prev, ...results }));
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs"
              >
                Mark all read
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 border-b cursor-pointer hover:bg-muted/50 transition-colors ${
                  !notification.is_read ? 'bg-muted/30' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{notification.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formattedMessages[notification.id] || notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <div className="w-2 h-2 bg-primary rounded-full mt-1 ml-2" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}